import * as React from "react";
import { X, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DEFAULT_ORGANIZER_AVATAR_SRC,
  DEFAULT_PLAYER_AVATAR_SRC,
} from "@mebi/ui";
import { profileService } from "@/features/common/services/profile.service";
import { useImageUpload } from "@/hooks/useImageUpload";
import UploadSourceModal from "@/components/ui/UploadSourceModal";

export interface AvatarUploadProps {
  /**
   * Current avatar URL or placeholder
   */
  avatarUrl?: string;

  /**
   * Function to handle when avatar is changed
   */
  onAvatarChange?: (
    file: File,
    previewUrl: string,
    errorMessage?: string,
  ) => void;

  /**
   * Function called when removing avatar
   */
  onAvatarRemove?: () => void;

  /**
   * Whether the avatar is editable or not
   */
  editable?: boolean;

  /**
   * Size of the avatar (in pixels)
   */
  size?: number;

  /**
   * Class name to be applied to the outer container
   */
  className?: string;

  /**
   * Additional title text to display
   */
  title?: string;

  /**
   * Maximum file size in MB
   */
  maxSizeMB?: number;

  /**
   * Upload in progress
   */
  isUploading?: boolean;

  /**
   * Allow remove avatar
   */
  allowRemove?: boolean;

  /**
   * Success state
   */
  isSuccess?: boolean;

  /**
   * Error message to display
   */
  errorMessage?: string;

  /**
   * User ID for avatar structure
   */
  userId?: string;

  /**
   * User role for avatar structure
   */
  role?: "Player" | "Organizer";
}

/**
 * AvatarUpload component - Upload avatar với MEBI Design System
 *
 * @example
 * ```tsx
 * <AvatarUpload
 *   title="Ảnh đại diện"
 *   onAvatarChange={handleAvatarChange}
 *   allowRemove={true}
 *   maxSizeMB={5}
 * />
 * ```
 */
const AvatarUpload = React.forwardRef<HTMLDivElement, AvatarUploadProps>(
  (
    {
      avatarUrl = "",
      onAvatarChange,
      onAvatarRemove,
      editable = true,
      size = 64,
      className,
      title,
      maxSizeMB = 5,
      isUploading = false,
      allowRemove = false,
      isSuccess = false,
      errorMessage,
      userId,
      role = "Player",
      ...props
    },
    ref,
  ) => {
    const fallbackAvatarSrc =
      role === "Organizer"
        ? DEFAULT_ORGANIZER_AVATAR_SRC
        : DEFAULT_PLAYER_AVATAR_SRC;

    const resolveAvatarUrl = React.useCallback(
      (url: string) => {
        const normalized = url.trim();
        if (!normalized) {
          return "";
        }

        // Skip resolution for URLs that are already directly displayable
        // (data: base64 previews, blob: object URLs, http/https remote URLs)
        if (
          /^(data:|blob:|https?:|file:\/\/|content:\/\/|capacitor:\/\/)/i.test(
            normalized,
          )
        ) {
          return normalized;
        }

        if (userId) {
          return profileService.getAvatarUrlWithStructure(
            userId,
            role,
            normalized,
          );
        }
        return profileService.getAvatarUrl(normalized);
      },
      [userId, role],
    );

    const {
      previewUrl,
      isOpen,
      setIsOpen,
      internalError,
      fileInputRef,
      openCamera,
      openGallery,
      acceptedTypes,
      handleFileInputChange,
      handlePreviewError,
      handleRemove,
    } = useImageUpload({
      initialUrl: avatarUrl,
      maxSizeMB,
      editable,
      isUploading,
      onChange: onAvatarChange,
      onRemove: onAvatarRemove,
      resolveUrl: resolveAvatarUrl,
    });

    const displayError = errorMessage || internalError;

    const handleCameraSelect = React.useCallback(() => {
      openCamera();
      setIsOpen(false);
    }, [openCamera, setIsOpen]);

    const handleGallerySelect = React.useCallback(() => {
      openGallery();
      setIsOpen(false);
    }, [openGallery, setIsOpen]);

    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex flex-col justify-start items-center gap-4",
          className,
        )}
        {...props}
      >
        {/* Title */}
        {title && (
          <div className="text-center text-neutral-900 text-base font-normal font-['Be_Vietnam_Pro'] leading-6">
            {title}
          </div>
        )}

        {/* Avatar container */}
        <div className="relative" style={{ width: size, height: size }}>
          {editable ? (
            <button
              type="button"
              className={cn(
                "w-full h-full rounded-full overflow-hidden border border-neutral-200 p-0 m-0 block",
                "bg-neutral-50",
                {
                  "cursor-pointer": !isUploading,
                  "cursor-not-allowed opacity-60": isUploading,
                },
              )}
              onClick={() => !isUploading && setIsOpen(true)}
              aria-label="Change avatar"
              disabled={isUploading}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                  onError={handlePreviewError}
                />
              ) : (
                <img
                  src={fallbackAvatarSrc}
                  alt={
                    role === "Organizer" ? "Organizer avatar" : "Player avatar"
                  }
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              )}

              {/* Loading overlay */}
              {isUploading && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-full">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </button>
          ) : (
            <div
              className={cn(
                "w-full h-full rounded-full overflow-hidden border border-neutral-200 p-0 m-0 block",
                "bg-neutral-50",
              )}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                  onError={handlePreviewError}
                />
              ) : (
                <img
                  src={fallbackAvatarSrc}
                  alt={
                    role === "Organizer" ? "Organizer avatar" : "Player avatar"
                  }
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              )}
            </div>
          )}

          {/* Success indicator */}
          {isSuccess && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-mebi-green-500 rounded-full flex items-center justify-center z-10 border border-white pointer-events-none">
              <Check className="w-3 h-3 text-white" />
            </div>
          )}

          {/* Error indicator */}
          {displayError && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-mebi-red-500 rounded-full flex items-center justify-center z-10 border border-white pointer-events-none">
              <X className="w-3 h-3 text-white" />
            </div>
          )}
        </div>

        {/* Action Link / Error Message */}
        {displayError ? (
          <div className="text-center justify-center text-mebi-red-600 text-sm font-normal font-['Be_Vietnam_Pro'] leading-5">
            {displayError}
          </div>
        ) : (
          editable && (
            <div className="h-5 flex-col justify-start items-start gap-2.5 flex">
              <button
                type="button"
                className="justify-center items-center gap-2.5 inline-flex flex-wrap content-center cursor-pointer bg-transparent border-none p-0"
                onClick={() => !isUploading && setIsOpen(true)}
              >
                <div className="text-center text-Color-Text-brand-default text-sm font-semibold font-['Be_Vietnam_Pro'] underline leading-5">
                  Chọn ảnh đại diện
                </div>
              </button>
            </div>
          )
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(",")}
          onChange={handleFileInputChange}
          className="sr-only"
        />

        <UploadSourceModal
          open={isOpen}
          onOpenChange={setIsOpen}
          title="Thêm tệp"
          description="Lựa chọn cách thức cập nhật ảnh đại diện: chụp ảnh mới hoặc chọn từ thư viện."
          cameraLabel="Chụp ảnh mới"
          galleryLabel="Chọn từ thư viện"
          onCamera={handleCameraSelect}
          onGallery={handleGallerySelect}
          showRemove={allowRemove && Boolean(previewUrl)}
          removeLabel="Xóa ảnh đại diện"
          onRemove={handleRemove}
        />
      </div>
    );
  },
);

AvatarUpload.displayName = "AvatarUpload";

export { AvatarUpload };
